import React, { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { FileDown, Hash } from "lucide-react";

const EXCLUDED_KEY = "orderflow_csv_export_excluded";
const RAW_KEY = "orderflow_csv_export_raw_numbers";

export function ExportCsvModal({ open, onClose, columns = [], rowCount = 0, onExport }) {
  const [selected, setSelected] = useState({});
  const [rawNumbers, setRawNumbers] = useState(false);

  // Restore last export choice (columns excluded + raw-numbers) from localStorage
  useEffect(() => {
    if (!open) return;
    let excluded = [];
    try { excluded = JSON.parse(localStorage.getItem(EXCLUDED_KEY) || "[]"); } catch { excluded = []; }
    const excludedSet = new Set(Array.isArray(excluded) ? excluded : []);
    const next = {};
    columns.forEach((c) => { next[c.name] = !excludedSet.has(c.name); });
    setSelected(next);
    setRawNumbers(localStorage.getItem(RAW_KEY) === "true");
  }, [open, columns]);

  const selectedNames = useMemo(
    () => columns.filter((c) => selected[c.name]).map((c) => c.name),
    [columns, selected]
  );
  const allChecked = columns.length > 0 && columns.every((c) => selected[c.name]);
  const toggleAll = () => {
    const next = {};
    columns.forEach((c) => { next[c.name] = !allChecked; });
    setSelected(next);
  };

  const handleExport = () => {
    // Persist the choice so it is pre-filled on the next export
    try {
      const excludedNow = columns.filter((c) => !selected[c.name]).map((c) => c.name);
      localStorage.setItem(EXCLUDED_KEY, JSON.stringify(excludedNow));
      localStorage.setItem(RAW_KEY, String(rawNumbers));
    } catch { /* ignore storage errors */ }
    onExport(selectedNames, rawNumbers);
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-hidden flex flex-col" data-testid="export-csv-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileDown className="w-5 h-5 text-emerald-600" />
            Exporter en CSV
            <span className="text-sm font-normal text-zinc-500">{rowCount} ligne(s) filtrée(s)</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex items-center justify-between px-1 mt-1">
          <span className="text-sm font-medium text-zinc-700">Colonnes à inclure</span>
          <label className="flex items-center gap-1.5 cursor-pointer text-xs text-zinc-600">
            <input type="checkbox" checked={allChecked} onChange={toggleAll} className="accent-emerald-600" data-testid="export-select-all" />
            Tout sélectionner
          </label>
        </div>

        <div className="flex-1 overflow-y-auto mt-2 border border-zinc-200 rounded-md p-2 grid grid-cols-2 gap-1">
          {columns.map((c) => (
            <label
              key={c.name}
              className={`flex items-center gap-2 px-2 py-1.5 rounded cursor-pointer text-sm transition-colors ${selected[c.name] ? "bg-emerald-50 text-zinc-800" : "text-zinc-500 hover:bg-zinc-50"}`}
              data-testid={`export-col-${c.name}`}
            >
              <input
                type="checkbox"
                checked={!!selected[c.name]}
                onChange={() => setSelected((prev) => ({ ...prev, [c.name]: !prev[c.name] }))}
                className="accent-emerald-600 flex-shrink-0"
              />
              <span className="truncate">{c.name}</span>
            </label>
          ))}
        </div>

        <label className="flex items-start gap-2 mt-3 p-2.5 rounded-md border border-zinc-200 bg-zinc-50 cursor-pointer" data-testid="export-raw-numbers">
          <input
            type="checkbox"
            checked={rawNumbers}
            onChange={(e) => setRawNumbers(e.target.checked)}
            className="accent-emerald-600 mt-0.5 flex-shrink-0"
          />
          <span className="text-sm">
            <span className="flex items-center gap-1.5 font-medium text-zinc-700"><Hash className="w-3.5 h-3.5" /> Nombres bruts</span>
            <span className="text-xs text-zinc-500">Exporte les montants en nombres purs (sans € ni espaces) pour les calculs Excel</span>
          </span>
        </label>

        <div className="flex justify-between items-center pt-3 border-t border-zinc-100 mt-3">
          <Button variant="outline" size="sm" onClick={onClose} data-testid="close-export-csv-modal">Annuler</Button>
          <Button
            size="sm"
            onClick={handleExport}
            disabled={selectedNames.length === 0}
            className="gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white"
            data-testid="confirm-export-csv-btn"
          >
            <FileDown className="w-4 h-4" />
            Exporter ({selectedNames.length} col.)
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
